import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from "@exhousing/design-system";
import { useState } from "react";
import { toast } from "sonner";

import { useAddMeterReading, useMeterReadings } from "@/hooks/api/useManagement";

const METER_LABELS: Record<string, string> = {
  electricity: "Electricity",
  gas: "Gas",
  water: "Water",
};

export function MeterReadingsCard({ propertyId }: { propertyId: string }) {
  const { data: readings } = useMeterReadings(propertyId);
  const add = useAddMeterReading(propertyId);
  const [meterType, setMeterType] = useState("electricity");
  const [value, setValue] = useState("");
  const today = new Date().toISOString().slice(0, 10);

  const onAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!value) return;
    add.mutate(
      { meter_type: meterType, reading_value: Number(value), reading_date: today },
      {
        onSuccess: () => setValue(""),
        onError: () => toast.error("Could not add reading"),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Meter readings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={onAdd} className="flex flex-wrap items-end gap-2">
          <select
            value={meterType}
            onChange={(e) => setMeterType(e.target.value)}
            className="h-11 rounded-md border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="electricity">Electricity</option>
            <option value="gas">Gas</option>
            <option value="water">Water</option>
          </select>
          <Input
            type="number"
            step="0.001"
            placeholder="Reading"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-32"
          />
          <Button type="submit" size="sm" variant="outline" disabled={add.isPending}>
            Add
          </Button>
        </form>

        {readings && readings.length > 0 ? (
          <ul className="space-y-1.5 text-sm">
            {readings.map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between border-b border-border/60 pb-1.5 last:border-0"
              >
                <span>{METER_LABELS[r.meter_type] ?? r.meter_type}</span>
                <span className="tabular-nums text-muted-foreground">
                  {r.reading_value} · {r.reading_date}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No readings yet.</p>
        )}
      </CardContent>
    </Card>
  );
}
